import { logAudit } from './audit';

const EDITABLE_SELECTOR = 'input, textarea, select, [contenteditable="true"], [contenteditable=""]';
const WATCH_SELECTOR = [
    '[class*="admin"]',
    '[class*="Admin"]',
    '[class*="chat"]',
    '[class*="Chat"]',
    '[class*="modal"]',
    '[class*="Modal"]',
    'button',
    'input',
    'textarea',
    'select',
    '[contenteditable]'
].join(',');

const CRITICAL_ATTRIBUTES = [
    'class',
    'style',
    'disabled',
    'readonly',
    'hidden',
    'type',
    'value',
    'checked',
    'contenteditable',
    'aria-disabled'
];

const isEditableTarget = (target) => {
    if (!target || typeof target.closest !== 'function') return false;
    return !!target.closest(EDITABLE_SELECTOR);
};

export const initFrontendDeterrents = () => {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
        return () => {};
    }

    if (window.__exchange37DeterrentsEnabled) {
        return () => {};
    }

    window.__exchange37DeterrentsEnabled = true;

    // На локалке (dev-сервер) НЕ блокируем выделение текста — оно должно
    // работать как обычно. В проде (собранный билд) защита остаётся.
    const isLocalDev = process.env.NODE_ENV === 'development';
    const lastAuditByAction = new Map();
    const lastMutationBySignature = new Map();
    let mutationObserver = null;

    const deterrentLabels = {
        shortcut_blocked: 'Заблокирована горячая клавиша',
        selection_blocked: 'Заблокировано выделение',
        drag_blocked: 'Заблокировано перетаскивание'
    };

    const auditDeterrent = (action, details = {}, cooldownMs = 1500) => {
        const now = Date.now();
        const lastAuditAt = lastAuditByAction.get(action) || 0;
        if (now - lastAuditAt < cooldownMs) return;
        lastAuditByAction.set(action, now);

        logAudit('frontend_deterrent', action, deterrentLabels[action] || 'Frontend deterrent triggered', {
            path: window.location.pathname,
            href: window.location.href,
            ...details
        });
    };

    const isIgnoredNode = (node) => {
        if (!node || node.nodeType !== Node.ELEMENT_NODE) return false;
        return !!node.closest?.('[data-audit-ignore="true"]');
    };

    const auditMutation = (details = {}) => {
        const now = Date.now();
        const signature = [
            details.mutationType || '',
            details.attribute || '',
            details.target?.tag || '',
            details.target?.id || '',
            details.target?.className || '',
            details.oldValue || '',
            details.newValue || ''
        ].join('|').slice(0, 500);
        const lastMutationAt = lastMutationBySignature.get(signature) || 0;
        if (now - lastMutationAt < 30000) return;
        lastMutationBySignature.set(signature, now);

        logAudit('frontend_deterrent', 'dom_mutation_suspected', 'Подозрительное изменение DOM', {
            path: window.location.pathname,
            href: window.location.href,
            ...details
        });
    };

    const getNodeSummary = (node) => {
        if (!node || node.nodeType !== Node.ELEMENT_NODE) {
            return null;
        }

        const text = node.textContent?.replace(/\s+/g, ' ').trim().slice(0, 160) || '';
        const className = typeof node.className === 'string' ? node.className.slice(0, 220) : '';

        return {
            tag: node.tagName,
            id: node.id || '',
            className,
            name: node.getAttribute('name') || '',
            type: node.getAttribute('type') || '',
            text
        };
    };

    const isWatchedNode = (node) => {
        if (!node || node.nodeType !== Node.ELEMENT_NODE) return false;
        if (isIgnoredNode(node)) return false;
        return node.matches?.(WATCH_SELECTOR) || !!node.closest?.(WATCH_SELECTOR);
    };

    const shouldLogMutation = (mutation) => {
        if (mutation.type === 'attributes') {
            const criticalAlways = ['disabled', 'readonly', 'hidden', 'type', 'contenteditable', 'aria-disabled'];
            return criticalAlways.includes(mutation.attributeName);
        }

        return false;
    };

    const handleMutations = (mutations) => {
        for (const mutation of mutations) {
            const target = mutation.target;
            if (!isWatchedNode(target)) continue;
            if (!shouldLogMutation(mutation)) continue;

            if (mutation.type === 'attributes') {
                auditMutation({
                    mutationType: 'attributes',
                    attribute: mutation.attributeName,
                    oldValue: mutation.oldValue,
                    newValue: target.getAttribute?.(mutation.attributeName),
                    target: getNodeSummary(target)
                });
                return;
            }

            if (mutation.type === 'characterData') {
                auditMutation({
                    mutationType: 'characterData',
                    oldValue: mutation.oldValue,
                    newValue: mutation.target?.textContent || '',
                    target: getNodeSummary(target.parentElement || target)
                });
                return;
            }

            if (mutation.type === 'childList') {
                const added = Array.from(mutation.addedNodes || []).filter(isWatchedNode).map(getNodeSummary).filter(Boolean).slice(0, 5);
                const removed = Array.from(mutation.removedNodes || []).filter(node => node.nodeType === Node.ELEMENT_NODE).map(getNodeSummary).filter(Boolean).slice(0, 5);

                if (added.length || removed.length) {
                    auditMutation({
                        mutationType: 'childList',
                        target: getNodeSummary(target),
                        added,
                        removed
                    });
                    return;
                }
            }
        }
    };

    const prevent = (event, action, details = {}) => {
        auditDeterrent(action, details);
        event.preventDefault();
        event.stopPropagation();
        return false;
    };

    const handleKeyDown = (event) => {
        const key = String(event.key || '').toLowerCase();
        const code = String(event.code || '').toLowerCase();
        const ctrlOrMeta = event.ctrlKey || event.metaKey;
        const blocked =
            key === 'f12' ||
            code === 'f12' ||
            (ctrlOrMeta && event.shiftKey && ['i', 'j', 'c', 'k'].includes(key)) ||
            (ctrlOrMeta && ['u', 's'].includes(key));

        if (blocked) {
            return prevent(event, 'shortcut_blocked', {
                key: event.key,
                code: event.code,
                ctrlKey: event.ctrlKey,
                metaKey: event.metaKey,
                shiftKey: event.shiftKey,
                altKey: event.altKey
            });
        }

        return true;
    };

    const handleSelectStart = (event) => {
        if (isEditableTarget(event.target)) return true;
        event.preventDefault();
        event.stopPropagation();
        return false;
    };

    const handleDragStart = (event) => {
        if (isEditableTarget(event.target)) return true;
        return prevent(event, 'drag_blocked', {
            target: event.target?.tagName || ''
        });
    };

    const originalConsole = {};
    ['log', 'info', 'debug', 'warn', 'error', 'table', 'dir', 'trace'].forEach((method) => {
        originalConsole[method] = console[method];
        try {
            console[method] = () => {};
        } catch (_) {
            // Some browsers can lock console methods.
        }
    });

    document.addEventListener('keydown', handleKeyDown, true);
    if (!isLocalDev) {
        document.addEventListener('selectstart', handleSelectStart, true);
    }
    document.addEventListener('dragstart', handleDragStart, true);

    mutationObserver = new MutationObserver(handleMutations);
    mutationObserver.observe(document.body, {
        subtree: true,
        childList: true,
        attributes: true,
        characterData: true,
        attributeOldValue: true,
        characterDataOldValue: true,
        attributeFilter: CRITICAL_ATTRIBUTES
    });

    return () => {
        document.removeEventListener('keydown', handleKeyDown, true);
        if (!isLocalDev) {
            document.removeEventListener('selectstart', handleSelectStart, true);
        }
        document.removeEventListener('dragstart', handleDragStart, true);
        mutationObserver?.disconnect();

        Object.entries(originalConsole).forEach(([method, original]) => {
            try {
                console[method] = original;
            } catch (_) {
                // ignore
            }
        });

        window.__exchange37DeterrentsEnabled = false;
    };
};
